/*
 * Design: Command Center — Dark Operations Dashboard
 * Direct Response Hero: Problem-first headline (Halbert style)
 * Specific proof numbers, urgency-driven CTA
 */

import { useEffect, useRef } from "react";
import { ArrowRight, MessageCircle, PhoneOff, Clock, DollarSign } from "lucide-react";

const HERO_BG = "https://private-us-east-1.manuscdn.com/sessionFile/dW8gB40GgASxDu6jN55O2b/sandbox/XIl8c8jcnZcHjQ3QUp7fZH-img-1_1770407080000_na1fn_aGVyby1iZw.png?x-oss-process=image/resize,w_1920,h_1920/format,webp/quality,q_80&Expires=1798761600&Policy=eyJTdGF0ZW1lbnQiOlt7IlJlc291cmNlIjoiaHR0cHM6Ly9wcml2YXRlLXVzLWVhc3QtMS5tYW51c2Nkbi5jb20vc2Vzc2lvbkZpbGUvZFc4Z0I0MEdnQVN4RHU2ak41NU8yYi9zYW5kYm94L1hJbDhjOGpjblpjSGpRM1FVcDdmWkgtaW1nLTFfMTc3MDQwNzA4MDAwMF9uYTFmbl9hR1Z5YnkxaVp3LnBuZz94LW9zcy1wcm9jZXNzPWltYWdlL3Jlc2l6ZSx3XzE5MjAsaF8xOTIwL2Zvcm1hdCx3ZWJwL3F1YWxpdHkscV84MCIsIkNvbmRpdGlvbiI6eyJEYXRlTGVzc1RoYW4iOnsiQVdTOkVwb2NoVGltZSI6MTc5ODc2MTYwMH19fV19&Key-Pair-Id=K2HSFNDJXOU9YS&Signature=jAao0Z8qAaiPoU0VdFxG02eFDbmd3jISFsX5G8jV6Y~n8auv0eddZrRRIdc8EwpNqhew2Erp5hpbnRIiRoWaVwhyyxxwz6NcggJqxGUaospchoVEbM3F95T2Ufz4AcxYsAdaNg3yOOE6Dd5O5bXoJy384fP8xLTMZrNMamV3oMy9naaczDd93KMNxWZlg6TJwAPboos70EE5c575FL4wZNvyDhyoGJB3o1voUXxvy7RKyDfUI7uuxS~OhVdXwquJmR9f1~slpMXH341QtM6m9Jfn1-eg6UbEcFwSLoFkdBnQOfrKh3J~~NGMewey7rODYYz6D0ehpxzaxAjCtlZRew__";

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const children = el.querySelectorAll("[data-animate]");
    // Defer animations until after page is interactive
    const scheduleAnimations = () => {
      children.forEach((child, i) => {
        setTimeout(() => {
          (child as HTMLElement).style.opacity = "1";
          (child as HTMLElement).style.transform = "translateY(0)";
        }, 200 + i * 150);
      });
    };
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(scheduleAnimations);
    } else {
      setTimeout(scheduleAnimations, 100);
    }
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#09090B]/95 via-[#09090B]/80 to-[#09090B]/40" />
      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#09090B] to-transparent" />

      <div ref={heroRef} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 sm:py-40">
        <div className="max-w-3xl">
          {/* Tag */}
          <div
            data-animate
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-red-500/20 bg-red-500/5 backdrop-blur-sm mb-8 opacity-0 translate-y-5 transition-all duration-700 ease-out"
          >
            <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
            <span className="text-red-300 text-sm font-medium">Your leads are dying in silence</span>
          </div>

          {/* Headline — Problem-first (Halbert) */}
          <h1
            data-animate
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.08] tracking-tight mb-6 opacity-0 translate-y-5 transition-all duration-700 ease-out"
          >
            You're Paying for Leads
            <br />
            <span className="gradient-text">Nobody Follows Up On</span>
          </h1>

          {/* Subheadline — Agitate + Solution */}
          <p
            data-animate
            className="text-lg sm:text-xl text-zinc-300 leading-relaxed max-w-xl mb-10 opacity-0 translate-y-5 transition-all duration-700 ease-out"
          >
            78% of deals go to the first responder. Your leads are going cold while your team sleeps, forgets, or gets busy. We build AI systems that respond in seconds, follow up for months, and never drop a lead.
          </p>

          {/* CTA Stack */}
          <div
            data-animate
            className="flex flex-col sm:flex-row gap-4 opacity-0 translate-y-5 transition-all duration-700 ease-out"
          >
            <a
              href="sms:+15039987760?body=Hi%20Luis%2C%20I%27m%20interested%20in%20learning%20more%20about%20your%20AI%20systems%20and%20GHL%20services."
              className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white rounded-lg transition-all duration-200 hover:opacity-90 hover:shadow-lg hover:shadow-purple-500/25 group"
              style={{
                background: "linear-gradient(135deg, #7C3AED, #D946EF)",
              }}
            >
              Text Me
              <MessageCircle size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            <button
              onClick={() => {
                const el = document.querySelector("#services");
                if (el) {
                  const top = el.getBoundingClientRect().top + window.scrollY - 80;
                  window.scrollTo({ top, behavior: "smooth" });
                }
              }}
              className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-zinc-300 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 hover:text-white transition-all duration-200"
            >
              See How It Works
            </button>
          </div>

          {/* Pain-point proof indicators */}
          <div
            data-animate
            className="flex flex-wrap items-center gap-6 mt-14 pt-8 border-t border-white/5 opacity-0 translate-y-5 transition-all duration-700 ease-out"
          >
            {[
              { icon: PhoneOff, value: "48%", label: "of leads never get a callback" },
              { icon: Clock, value: "5 min", label: "response time = 21x more conversions" },
              { icon: DollarSign, value: "$0", label: "revenue from forgotten leads" },
            ].map((stat) => (
              <div key={stat.label} className="flex items-center gap-3">
                <stat.icon size={16} className="text-purple-400" />
                <span className="text-white font-bold text-sm">{stat.value}</span>
                <span className="text-zinc-500 text-xs">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
